import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { EmailServiceConfig } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaEdit, FaTrash, FaPlus, FaFilter } from "react-icons/fa";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";

// Component for managing email services with add, edit, delete, multiple delete, and filter functionality
export default function EmailService() {
  const { userData } = useAuth();
  const [emailServices, setEmailServices] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    id: [],
    mailservice: [],
    from_mail: [],
    password: [],
    teammailid: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const columnDisplay = {
    id: "ID",
    mailservice: "Mail Service",
    from_mail: "From Email",
    password: "Password",
    teammailid: "Team Mail ID",
  };

  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = emailServices.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort(
      (a, b) =>
        column === "id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters to an item
  const applyFilters = (item) => {
    return (
      (filters.id.length === 0 || filters.id.includes(item.id)) &&
      (filters.mailservice.length === 0 || filters.mailservice.includes(item.mailservice)) &&
      (filters.from_mail.length === 0 || filters.from_mail.includes(item.from_mail)) &&
      (filters.password.length === 0 || filters.password.includes(item.password)) &&
      (filters.teammailid.length === 0 || filters.teammailid.includes(item.teammailid))
    );
  };

  // Filter email services
  const filteredEmailServices = emailServices.filter(applyFilters);

  const totalPages = Math.ceil(filteredEmailServices.length / itemsPerPage);
  const paginatedEmailServices = filteredEmailServices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Fetch all email services on component mount
  useEffect(() => {
    const fetchEmailServices = async () => {
      try {
        const response = await fetch(`${EmailServiceConfig}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch email services");
        }
        const data = await response.json();
        setEmailServices(data);
      } catch (error) {
        console.error("Error fetching email services:", error);
        Swal.fire("Error", "Failed to fetch email services. Please try again.", "error");
      }
    };
    fetchEmailServices();
  }, [userData.access_token]);

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Select all filtered email services
  const handleSelectAll = () => {
    if (selectedRows.length === filteredEmailServices.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredEmailServices.map((item) => item.id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Do you want to delete ${selectedRows.length} selected email services?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await Promise.all(
            selectedRows.map(async (id) => {
              const response = await fetch(`${EmailServiceConfig}/delete/${id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                  Accept: "application/json",
                },
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error("Server Error:", errorText);
                throw new Error(`Failed to delete email service ${id}`);
              }
            })
          );

          const updatedEmailServices = emailServices.filter(
            (service) => !selectedRows.includes(service.id)
          );
          setEmailServices(updatedEmailServices);
          setSelectedRows([]);

          Swal.fire(
            "Success!",
            "Selected email services deleted successfully",
            "success"
          );
        } catch (error) {
          console.error("Error deleting email services:", error);
          Swal.fire(
            "Error",
            "Failed to delete some email services. Please try again.",
            "error"
          );
        }
      }
    });
  };

  // Function to add a new email service using SweetAlert for input
  const addEmailService = async () => {
    const { value } = await Swal.fire({
      title: "Create new Email Service",
      html:
        '<input id="swal-mailservice" class="swal2-input" placeholder="Enter mail service">' +
        '<input id="swal-from_mail" class="swal2-input" placeholder="Enter from email">' +
        '<input id="swal-password" class="swal2-input" placeholder="Enter password">' +
        '<input id="swal-teammailid" class="swal2-input" placeholder="Enter team mail ID">',
      focusConfirm: false,
      showCancelButton: true,
      preConfirm: () => {
        const mailservice = document.getElementById("swal-mailservice").value;
        const from_mail = document.getElementById("swal-from_mail").value;
        const password = document.getElementById("swal-password").value;
        const teammailid = document.getElementById("swal-teammailid").value;
        if (!mailservice || !from_mail || !password) {
          Swal.showValidationMessage("Mail service, from email, and password are required!");
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(from_mail)) {
          Swal.showValidationMessage("Please enter a valid from email!");
          return false;
        }
        if (teammailid && !/\S+@\S+\.\S+/.test(teammailid)) {
          Swal.showValidationMessage("Please enter a valid team mail ID or leave it empty!");
          return false;
        }
        return { mailservice, from_mail, password, teammailid };
      },
    });

    if (value) {
      try {
        const response = await fetch(`${EmailServiceConfig}/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
          body: JSON.stringify({
            mailservice: value.mailservice,
            from_mail: value.from_mail,
            password: value.password,
            teammailid: value.teammailid,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Create Error Response:", errorText);
          throw new Error("Failed to add email service");
        }

        const newEmailService = await response.json();
        setEmailServices([...emailServices, newEmailService]);
        Swal.fire("Success!", "Email service added successfully.", "success");
      } catch (error) {
        console.error("Error adding email service:", error);
        Swal.fire("Error", "Failed to add email service. Please try again.", "error");
      }
    }
  };

  // Function to edit an existing email service using SweetAlert for input
  const editEmailService = async (service) => {
    const { value } = await Swal.fire({
      title: "Edit Email Service",
      html:
        `<input id="swal-mailservice" class="swal2-input" placeholder="Enter mail service" value="${service.mailservice}">` +
        `<input id="swal-from_mail" class="swal2-input" placeholder="Enter from email" value="${service.from_mail}">` +
        `<input id="swal-password" class="swal2-input" placeholder="Enter password" value="${service.password}">` +
        `<input id="swal-teammailid" class="swal2-input" placeholder="Enter team mail ID" value="${service.teammailid || ''}">`,
      focusConfirm: false,
      showCancelButton: true,
      preConfirm: () => {
        const mailservice = document.getElementById("swal-mailservice").value;
        const from_mail = document.getElementById("swal-from_mail").value;
        const password = document.getElementById("swal-password").value;
        const teammailid = document.getElementById("swal-teammailid").value;
        if (!mailservice || !from_mail || !password) {
          Swal.showValidationMessage("Mail service, from email, and password are required!");
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(from_mail)) {
          Swal.showValidationMessage("Please enter a valid from email!");
          return false;
        }
        if (teammailid && !/\S+@\S+\.\S+/.test(teammailid)) {
          Swal.showValidationMessage("Please enter a valid team mail ID or leave it empty!");
          return false;
        }
        return { mailservice, from_mail, password, teammailid };
      },
    });

    if (value) {
      try {
        const response = await fetch(`${EmailServiceConfig}/update/${service.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
          body: JSON.stringify({
            mailservice: value.mailservice,
            from_mail: value.from_mail,
            password: value.password,
            teammailid: value.teammailid,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Update Error Response:", errorText);
          throw new Error("Failed to update email service");
        }

        const updatedEmailService = await response.json();
        setEmailServices(
          emailServices.map((item) =>
            item.id === service.id ? updatedEmailService : item
          )
        );
        Swal.fire("Success!", "Email service updated successfully.", "success");
      } catch (error) {
        console.error("Error updating email service:", error);
        Swal.fire("Error", "Failed to update email service. Please try again.", "error");
      }
    }
  };

  // Function to delete a single email service with confirmation
  const deleteEmailService = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${EmailServiceConfig}/delete/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete email service");
        }

        setEmailServices(emailServices.filter((service) => service.id !== id));
        Swal.fire("Deleted!", "Email service has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting email service:", error);
        Swal.fire(
          "Error",
          "Failed to delete email service. Please try again.",
          "error"
        );
      }
    }
  };

  // Render filter popup for a column
  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (val) =>
        val != null &&
        val.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) =>
      tempSelected.includes(val)
    );

    const handleToggleVisible = (e) => {
      if (e.target.checked) {
        setTempSelected([...new Set([...tempSelected, ...filteredValues])]);
      } else {
        setTempSelected(
          tempSelected.filter((val) => !filteredValues.includes(val))
        );
      }
    };

    const clearSelections = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column]: [],
      }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div
        className="filter-popup"
        style={{
          position: "absolute",
          top: "100%",
          left: "0",
          zIndex: 100,
        }}
      >
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleVisible}
          />
          <input
            className="filter-search"
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <span className="clear-search" onClick={() => setSearchInput("")}>
              ×
            </span>
          )}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setTempSelected([...tempSelected, val]);
                    } else {
                      setTempSelected(
                        tempSelected.filter((v) => v !== val)
                      );
                    }
                  }}
                />
                {val != null ? val.toString() : "N/A"}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
              <title>Email Services - SMTS Admin Panel</title>
            </Helmet>
      <div className="page-title">
        <h2>Email Services</h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredEmailServices.length &&
                    filteredEmailServices.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                ID
                <FaFilter
                  className={`filter-icon ${
                    filters.id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "id" && renderFilterPopup("id")}
              </th>
              <th style={{ position: "relative" }}>
                Mail Service
                <FaFilter
                  className={`filter-icon ${
                    filters.mailservice.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("mailservice")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "mailservice" && renderFilterPopup("mailservice")}
              </th>
              <th style={{ position: "relative" }}>
                From Email
                <FaFilter
                  className={`filter-icon ${
                    filters.from_mail.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("from_mail")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "from_mail" && renderFilterPopup("from_mail")}
              </th>
              <th style={{ position: "relative" }}>
                Password
                <FaFilter
                  className={`filter-icon ${
                    filters.password.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("password")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "password" && renderFilterPopup("password")}
              </th>
              <th style={{ position: "relative" }}>
                Team Mail ID
                <FaFilter
                  className={`filter-icon ${
                    filters.teammailid.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("teammailid")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "teammailid" && renderFilterPopup("teammailid")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedEmailServices.map((service) => (
              <tr
                key={service.id}
                className={selectedRows.includes(service.id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(service.id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(service.id)}
                    onChange={() => toggleRowSelection(service.id)}
                  />
                </td>
                <td>{service.id}</td>
                <td>{service.mailservice}</td>
                <td>{service.from_mail}</td>
                <td>{service.password}</td>
                <td>{service.teammailid || "N/A"}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => editEmailService(service)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaEdit />
                  </button>
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteEmailService(service.id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addEmailService}>
        <FaPlus />
      </button>
    </div>
  );
}